import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { isWorkspaceEnabledForUser } from "@/utils/workspaceFeatureFlag";
import { getWorkspace } from "../../../services/workspacesApi";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { WorkspaceCanvasOverlays } from "./WorkspaceCanvasOverlays";
import { WorkspaceErrorBoundary } from "./WorkspaceErrorBoundary";
import { WorkspaceProvider, useWorkspaceContext } from "./workspaceContext";
import { useWorkspaceAccountScope } from "./useWorkspaceAccountScope";
import { WORKSPACE_HUB_BREADCRUMB } from "./workspaceRoutes";

function WorkspaceCanvasContent(): JSX.Element {
  const { detail, loading, error, refreshDetail, scope } = useWorkspaceContext();
  const pageMinHeight = statisticsPageMinHeight(useShadcnInsetChrome());

  useShadcnInsetDynamicPageTitle(
    detail?.name?.trim() ||
      detail?.brokerAccountCode?.trim() ||
      scope.brokerAccountCode.trim() ||
      "Workspace canvas",
    { parent: WORKSPACE_HUB_BREADCRUMB }
  );

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  if (loading && !detail) {
    return (
      <div
        data-workspace-canvas-page=""
        className="flex w-full flex-col items-center justify-center gap-3 py-16"
      >
        <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
        <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          Loading workspace…
        </p>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div
        data-workspace-canvas-page=""
        className="flex w-full flex-col items-center justify-center px-4 py-16"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState variant="no-data" title="Workspace unavailable" description={error} />
      </div>
    );
  }

  return (
    <div data-workspace-canvas-page="" className="flex min-h-0 w-full flex-1 flex-col">
      {error ? (
        <Alert variant="warning" className="m-4 mb-0 shrink-0">
          <TriangleAlert />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <WorkspaceErrorBoundary onReset={() => void refreshDetail()}>
        <WorkspaceCanvasOverlays />
      </WorkspaceErrorBoundary>
    </div>
  );
}

export default function WorkspaceCanvasPage(): JSX.Element {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { scope, ready, loading: accountLoading } = useWorkspaceAccountScope();
  const { isAdmin, isLoading: flagsLoading } = useFeatureFlags();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);

  if (!flagsLoading && !isWorkspaceEnabledForUser(isAdmin)) {
    return (
      <div
        data-workspace-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Workspace not available"
          description="Workspace is not enabled for your account yet."
        />
      </div>
    );
  }

  if (accountLoading || flagsLoading) {
    return (
      <div
        data-workspace-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="loading"
          title="Loading account…"
          description="Preparing workspace canvas."
        />
      </div>
    );
  }

  if (!ready || !scope || !workspaceId?.trim()) {
    return (
      <div
        data-workspace-canvas-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Workspace unavailable"
          description="Select an account and open a workspace from the hub."
        />
      </div>
    );
  }

  return (
    <WorkspaceProvider
      scope={scope}
      workspaceId={workspaceId.trim()}
      loadDetail={getWorkspace}
    >
      <WorkspaceCanvasContent />
    </WorkspaceProvider>
  );
}
